import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatExactMinorUnits } from '@norvix/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  customersApi,
  type CustomerPaymentRow,
  type PaymentSource,
} from '../../api/customers.api';
import { APP_LOCALE } from '../../utils/locale.js';

/**
 * The client profile's payment history — who paid, their reference, their
 * account, and the amount, for every payment in and out, newest first.
 *
 * Tracker #43. The old Transactions tab was a ledger dump: an id and an amount,
 * nothing an analyst reviewing a client can act on. The detail was always held,
 * spread across four tables, but nothing read it by customer. This does, and
 * shows it. Counterparty accounts are masked, revealed one at a time through an
 * audited call — the same treatment as a customer's masked PII.
 */

const PAGE = 25;

const SOURCE_LABEL: Record<PaymentSource, string> = {
  SEPA: 'SEPA / internal',
  SWIFT: 'SWIFT',
  INBOUND: 'Inbound',
  WITHDRAWAL: 'Withdrawal',
  FEE: 'Fee',
};

function formatCents(cents: string, currency: string): string {
  try {
    return `${formatExactMinorUnits(BigInt(cents), currency, true)} ${currency}`;
  } catch {
    return `${cents} ${currency}`;
  }
}

interface CustomerPaymentsTabProps {
  customerId: string;
  /** Whether this operator may unmask a counterparty account (ADMIN / COMPLIANCE). */
  canReveal: boolean;
}

function rowKey(row: CustomerPaymentRow): string {
  return `${row.source}:${row.id}`;
}

export function CustomerPaymentsTab({ customerId, canReveal }: CustomerPaymentsTabProps): JSX.Element {
  const [rows, setRows] = useState<CustomerPaymentRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [degraded, setDegraded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showFees, setShowFees] = useState(true);
  const requestSequence = useRef(0);

  // Full accounts revealed so far, and the ones being revealed right now.
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState<Record<string, boolean>>({});

  // The customer whose response we still care about. The parent mounts a fresh
  // instance per customer (key={customerId}), but a "Load more" already in
  // flight when this unmounts must not write its rows into the next customer's
  // view — so a stale response is dropped rather than applied.
  const wantedFor = useRef(customerId);
  useEffect(() => {
    wantedFor.current = customerId;
    return () => {
      wantedFor.current = '';
    };
  }, [customerId]);

  const load = useCallback(
    async (from: number) => {
      const sequence = ++requestSequence.current;
      setLoading(true);
      setError(false);
      try {
        const page = await customersApi.payments(customerId, PAGE, from, showFees);
        if (wantedFor.current !== customerId || sequence !== requestSequence.current) return;
        setRows((prev) => (from === 0 ? page.items : [...prev, ...page.items]));
        setHasMore(page.hasMore);
        setTruncated(page.truncated);
        setDegraded(page.degraded);
        setOffset(from + page.items.length);
      } catch {
        if (wantedFor.current !== customerId || sequence !== requestSequence.current) return;
        setError(true);
      } finally {
        if (wantedFor.current === customerId && sequence === requestSequence.current) setLoading(false);
      }
    },
    [customerId, showFees],
  );

  useEffect(() => {
    setRows([]);
    setOffset(0);
    setHasMore(false);
    setTruncated(false);
    void load(0);
    return () => { requestSequence.current++; };
  }, [load]);

  const reveal = useCallback(
    async (row: CustomerPaymentRow) => {
      const key = rowKey(row);
      setRevealing((prev) => ({ ...prev, [key]: true }));
      try {
        const { iban } = await customersApi.revealPaymentIban(customerId, row.source, row.id);
        setRevealed((prev) => ({ ...prev, [key]: iban }));
      } catch {
        // Leave it masked; the operator can try again.
      } finally {
        setRevealing((prev) => ({ ...prev, [key]: false }));
      }
    },
    [customerId],
  );

  const feeToggle = <FormControlLabel label="Show fees" control={<Switch checked={showFees}
    onChange={(_, checked) => setShowFees(checked)} />} />;

  if (loading && rows.length === 0) {
    return (
      <Stack spacing={2}>{feeToggle}<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box></Stack>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        {feeToggle}
        <Alert severity="error">Could not load this customer’s transaction history.</Alert>
        <Button variant="outlined" size="small" onClick={() => void load(0)}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <Stack spacing={2}>{feeToggle}
        {degraded && <Alert severity="warning">Some transaction sources did not respond. This history may be incomplete.</Alert>}
        <Typography variant="body2" color="text.secondary">
          {showFees ? 'No payments or posted fees recorded for this customer yet.' : 'No payments recorded. Fee transactions are hidden.'}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0} component={Paper} sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1} sx={{ px: 2.5, py: 1.5 }}>
      {feeToggle}
      <Typography variant="caption" color="text.secondary">
        Payments{showFees ? ', posted fee charges and refunds' : ' (fee transactions hidden)'}, newest first.
        Counterparty accounts are masked; reveal one to see it in full — each reveal
        is logged.
      </Typography>
      </Stack>
      {degraded ? (
        <Alert severity="warning">
          One of the transaction sources did not respond, so this history may be incomplete. Refresh in
          a moment for the full picture.
        </Alert>
      ) : null}
      <Box sx={{ overflowX: 'auto' }}><Table size="small" aria-label="Customer transactions" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Movement / rail</TableCell>
            <TableCell>Counterparty / account</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const key = rowKey(row);
            const full = revealed[key];
            return (
              <TableRow key={key}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(row.occurredAt).toLocaleDateString(APP_LOCALE)}
                  <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.5 }}>{new Date(row.occurredAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={row.direction === 'IN' ? 'success' : 'default'}
                    label={row.source === 'FEE' ? (row.direction === 'IN' ? 'Commission refund' : 'Commission') : row.direction === 'IN' ? 'Incoming' : 'Outgoing'}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">{row.direction === 'IN' ? 'Credit' : 'Debit'} · {row.source === 'FEE' ? 'Norvix' : SOURCE_LABEL[row.source]}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 220, maxWidth: 320, overflowWrap: 'anywhere' }}>
                  {row.counterpartyCustomerId !== undefined ? (
                    <>
                      <MuiLink
                        component={Link}
                        to={`/customers/${row.counterpartyCustomerId}`}
                        underline="hover"
                        color="inherit"
                        fontWeight={600}
                        variant="body2"
                      >
                        {row.counterpartyName ?? (row.counterpartyCustomerName || 'Norvix client')}
                      </MuiLink>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Norvix client{row.counterpartyCustomerName && row.counterpartyCustomerName !== row.counterpartyName ? ` · ${row.counterpartyCustomerName}` : ''}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2">{row.counterpartyName ?? '—'}</Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                  {row.source === 'FEE' ? (
                    <Typography variant="caption" color="text.secondary" title={row.feeAccountId}>
                      Customer account {row.feeAccountId?.slice(0, 8)}…
                    </Typography>
                  ) : row.counterpartyIban === null ? (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  ) : full !== undefined ? (
                    <code style={{ fontSize: '0.72rem' }}>{full}</code>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <code style={{ fontSize: '0.72rem' }}>{row.counterpartyIban}</code>
                      {canReveal ? (
                        <Button
                          size="small"
                          variant="text"
                          className="workspace-link"
                          disabled={revealing[key] === true}
                          onClick={() => void reveal(row)}
                        >
                          {revealing[key] === true ? '…' : 'Reveal'}
                        </Button>
                      ) : null}
                    </Stack>
                  )}
                  </Box>
                </TableCell>
                <TableCell sx={{ maxWidth: 300, overflowWrap: 'anywhere' }}><Typography variant="body2" fontWeight={row.source === 'FEE' ? 600 : 400}>{row.reference ?? '—'}</Typography></TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  <Typography
                    variant="body2"
                    color={row.direction === 'IN' ? 'success.main' : 'text.primary'}
                  >
                    {row.direction === 'IN' ? '+' : '−'}
                    {formatCents(row.amountCents, row.currency)}
                  </Typography>
                </TableCell>
                <TableCell>{row.status === 'POSTED' ? 'Posted' : row.status}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table></Box>

      <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap" sx={{ px: 2.5, py: 2 }}>
        {hasMore ? (
          <Button variant="outlined" size="small" disabled={loading} onClick={() => void load(offset)}>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        ) : truncated ? null : (
          <Typography variant="caption" color="text.secondary">
            {degraded ? 'Some transaction sources are unavailable.' : showFees ? 'All recorded payments and posted fees shown.' : 'All recorded payments shown. Fee transactions are hidden.'}
          </Typography>
        )}
        {error ? (
          <Typography variant="caption" color="error">
            Could not load more — try again.
          </Typography>
        ) : null}
      </Stack>

      {truncated ? (
        <Alert severity="info">
          This client has a very large number of payments; the oldest may not be shown. Narrow by
          date in the payments queue if you need them.
        </Alert>
      ) : null}
    </Stack>
  );
}
